import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Box, CircularProgress, Typography } from '@mui/material';
import axios from 'axios';
import { RootState, AppDispatch } from '../store/store';
import { fetchCustomerProfile } from '../store/slices/customerAuthSlice';

/**
 * Props for ProtectedRoute component
 */
interface ProtectedRouteProps {
  /** Content to render when authenticated */
  children: React.ReactNode;
  /** Type of authentication required */
  requiredAuth: 'customer' | 'engineer';
}

/**
 * ProtectedRoute - Wraps routes that require authentication
 *
 * This component provides centralized route protection by:
 * 1. ALWAYS validating auth server-side via fetchCustomerProfile (httpOnly cookies)
 * 2. Showing a loading spinner during validation
 * 3. Redirecting to login on auth failure (preserving intended destination)
 *
 * SECURITY: Server-side validation is MANDATORY - we never trust client-side
 * Redux state alone. This prevents bypass attacks via browser console state
 * manipulation. The httpOnly cookies are sent automatically by the browser,
 * and the server validates the actual token.
 *
 * @example
 * ```tsx
 * <Route
 *   path="/customer/dashboard"
 *   element={
 *     <ProtectedRoute requiredAuth="customer">
 *       <CustomerDashboard />
 *     </ProtectedRoute>
 *   }
 * />
 * ```
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredAuth }) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  // Track validation state
  const [isValidating, setIsValidating] = useState(true);
  const [validationFailed, setValidationFailed] = useState(false);

  // Prevent double validation in React strict mode
  const hasValidated = useRef(false);

  // Customer auth state from Redux - used ONLY after server validation completes
  // SECURITY: isAuthenticated is set by fetchCustomerProfile after server validates the httpOnly cookie
  const { isAuthenticated, loading: authLoading } = useSelector(
    (state: RootState) => state.customerAuth
  );

  useEffect(() => {
    // Skip if already validated in this component instance
    if (hasValidated.current) {
      return;
    }

    // Track if component is still mounted for state updates
    let isMounted = true;
    // Store thunk promise for abort on unmount (Task #4212)
    // Type: ReturnType of dispatch(fetchCustomerProfile()) which is an AsyncThunkAction with abort method
    let profilePromise: { abort: () => void } | null = null;

    const validateAuth = async () => {
      hasValidated.current = true;

      // SECURITY: ALWAYS validate server-side, never trust Redux state alone
      // This prevents bypass attacks via browser console state manipulation.
      // The server validates the httpOnly cookie token - this is the source of truth.
      try {
        if (requiredAuth === 'customer') {
          // Store the promise so we can abort it on unmount
          const thunkPromise = dispatch(fetchCustomerProfile());
          profilePromise = thunkPromise;
          await thunkPromise.unwrap();
          // Only update state if still mounted
          if (isMounted) {
            setIsValidating(false);
          }
        } else if (requiredAuth === 'engineer') {
          const token = localStorage.getItem('accessToken');
          if (!token) {
            if (isMounted) {
              setValidationFailed(true);
              setIsValidating(false);
            }
          } else {
            try {
              await axios.get('/api/v1/engineer/sessions', {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 5000,
              });
              if (isMounted) {
                setIsValidating(false);
              }
            } catch {
              if (isMounted) {
                setValidationFailed(true);
                setIsValidating(false);
              }
            }
          }
        }
      } catch {
        // Auth validation failed (401/403 or network error)
        // User needs to login - only update state if still mounted
        if (isMounted) {
          setValidationFailed(true);
          setIsValidating(false);
        }
      }
    };

    validateAuth();

    // Cleanup: abort any in-flight requests on unmount (Task #4212)
    return () => {
      isMounted = false;
      if (profilePromise) {
        profilePromise.abort();
      }
    };
  }, [dispatch, requiredAuth]);

  // Show loading spinner during validation
  if (isValidating || authLoading) {
    return (
      <Box
        role="status"
        aria-live="polite"
        aria-busy="true"
        aria-label="Verifying authentication"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress aria-hidden="true" />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Verifying authentication...
        </Typography>
      </Box>
    );
  }

  // Validation failed or not authenticated - redirect to login
  if (validationFailed || !isAuthenticated) {
    // Determine login path based on auth type
    const loginPath = requiredAuth === 'customer' ? '/customer/login' : '/login';

    // Redirect to login, preserving the intended destination
    return <Navigate to={loginPath} state={{ from: location.pathname }} replace />;
  }

  // Authenticated - render protected content
  return <>{children}</>;
};

export default ProtectedRoute;
